/**
 * كتالوج طلبات الموظّفين.
 *
 * ما يطلبه الموظّفُ من إدارته ولا يمسّ رصيدَ إجازته: إذنُ ساعتين، ومصروفٌ
 * دفعه من جيبه، وخطابٌ للبنك، وعهدةٌ يطلبها. والإجازاتُ لها بابُها
 * ومحرّكُها — هي وحدَها ما يخصم رصيدًا ويدخل حسابَ المرتّب.
 *
 * والقواعدُ تُضبط لكلّ نوعٍ ولا تُكتب في الشيفرة: شركةٌ تسمح بإذنين في
 * الشهر وأخرى بأربعة، وشركةٌ تشترط مرفقًا على المصروفات وأخرى لا.
 */
import type { Labelled } from './common.js';

/* ==========================================================================
   أنواع الطلبات
   ========================================================================== */

export const REQUEST_KINDS = [
  'PERMISSION',
  'EXPENSE',
  'LETTER',
  'CUSTODY',
  'OTHER',
] as const;
export type RequestKind = (typeof REQUEST_KINDS)[number];

export interface RequestKindDef extends Labelled<RequestKind> {
  description: string;
  /** ما يقترحه هذا النوعُ من حقولٍ حين يُنشأ — تُعدَّل بعده. */
  suggests: {
    needsDates: boolean;
    needsHours: boolean;
    needsAmount: boolean;
    needsAttachment: boolean;
  };
}

export const REQUEST_KIND_DEFS: readonly RequestKindDef[] = [
  {
    key: 'PERMISSION',
    label: 'إذن',
    kind: 'info',
    description: 'خروجٌ أو تأخّرٌ بساعاتٍ في يومٍ بعينه، لا يومًا كاملًا.',
    suggests: {
      needsDates: true,
      needsHours: true,
      needsAmount: false,
      needsAttachment: false,
    },
  },
  {
    key: 'EXPENSE',
    label: 'مصروف',
    kind: 'warning',
    description: 'مالٌ دفعه الموظّف من جيبه ويُردّ إليه.',
    suggests: {
      needsDates: true,
      needsHours: false,
      needsAmount: true,
      needsAttachment: true,
    },
  },
  {
    key: 'LETTER',
    label: 'خطاب',
    kind: 'brand',
    description: 'خطابُ تعريفٍ للبنك أو للسفارة أو لجهةٍ تطلبه.',
    suggests: {
      needsDates: false,
      needsHours: false,
      needsAmount: false,
      needsAttachment: false,
    },
  },
  {
    key: 'CUSTODY',
    label: 'عهدة',
    kind: 'neutral',
    description: 'جهازٌ أو أداةٌ يطلبها الموظّف لعمله.',
    suggests: {
      needsDates: false,
      needsHours: false,
      needsAmount: false,
      needsAttachment: false,
    },
  },
  {
    key: 'OTHER',
    label: 'أخرى',
    kind: 'neutral',
    description: 'ما لا يندرج تحت ما سبق.',
    suggests: {
      needsDates: false,
      needsHours: false,
      needsAmount: false,
      needsAttachment: false,
    },
  },
] as const;

export const REQUEST_KIND_MAP = Object.fromEntries(
  REQUEST_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<RequestKind, RequestKindDef>>;

/* ==========================================================================
   حالة الطلب
   ========================================================================== */

export const REQUEST_STATUSES = [
  'PENDING',
  'APPROVED',
  'REJECTED',
  'CANCELLED',
] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];

export interface RequestStatusDef extends Labelled<RequestStatus> {
  /** لم يُبتّ فيه بعد. */
  open: boolean;
}

export const REQUEST_STATUS_DEFS: readonly RequestStatusDef[] = [
  { key: 'PENDING', label: 'قيد النظر', kind: 'warning', open: true },
  { key: 'APPROVED', label: 'موافَق عليه', kind: 'success', open: false },
  { key: 'REJECTED', label: 'مرفوض', kind: 'danger', open: false },
  { key: 'CANCELLED', label: 'مسحوب', kind: 'neutral', open: false },
] as const;

export const REQUEST_STATUS_MAP = Object.fromEntries(
  REQUEST_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<RequestStatus, RequestStatusDef>>;

/* ==========================================================================
   البذور
   ========================================================================== */

export interface RequestTypeSeed {
  key: string;
  label: string;
  kind: RequestKind;
  description: string;
  needsDates: boolean;
  needsHours: boolean;
  needsAmount: boolean;
  needsAttachment: boolean;
  maxPerMonth?: number;
  maxHours?: number;
  minNoticeDays?: number;
  approverRole?: 'MANAGER' | 'ADMIN';
}

/**
 * أنواعٌ تُزرع عند أوّل فتحٍ للشاشة.
 *
 * لا يقف مسؤولُ الموارد البشرية أمام قائمةٍ فارغةٍ فيظنّ الشاشةَ معطوبة.
 * وكلُّها تُعدَّل وتُوقف — البذرةُ اقتراحٌ لا حكم.
 */
export const DEFAULT_REQUEST_TYPES: readonly RequestTypeSeed[] = [
  {
    key: 'permission',
    label: 'إذن انصراف',
    kind: 'PERMISSION',
    description: 'خروجٌ قبل نهاية الدوام بساعاتٍ معدودة.',
    needsDates: true,
    needsHours: true,
    needsAmount: false,
    needsAttachment: false,
    maxPerMonth: 2,
    maxHours: 4,
  },
  {
    key: 'late-permission',
    label: 'إذن تأخير',
    kind: 'PERMISSION',
    description: 'حضورٌ بعد موعد بدء الدوام بساعاتٍ معدودة.',
    needsDates: true,
    needsHours: true,
    needsAmount: false,
    needsAttachment: false,
    maxPerMonth: 2,
    maxHours: 3,
  },
  {
    key: 'expense',
    label: 'ردّ مصروفات',
    kind: 'EXPENSE',
    description: 'مواصلاتٌ أو شراءٌ لحساب الشركة — بالإيصال.',
    needsDates: true,
    needsHours: false,
    needsAmount: true,
    needsAttachment: true,
    approverRole: 'ADMIN',
  },
  {
    key: 'bank-letter',
    label: 'خطاب تعريف للبنك',
    kind: 'LETTER',
    description: 'يذكر المسمّى وتاريخ التعيين والراتب.',
    needsDates: false,
    needsHours: false,
    needsAmount: false,
    needsAttachment: false,
    minNoticeDays: 2,
  },
  {
    key: 'custody-request',
    label: 'طلب عهدة',
    kind: 'CUSTODY',
    description: 'جهازٌ أو أداةٌ يحتاجها الموظّف لعمله.',
    needsDates: false,
    needsHours: false,
    needsAmount: false,
    needsAttachment: false,
  },
] as const;

/* ==========================================================================
   القواعد
   ========================================================================== */

export interface RequestRules {
  needsDates: boolean;
  needsHours: boolean;
  needsAmount: boolean;
  needsReason: boolean;
  needsAttachment: boolean;
  maxPerMonth: number | null;
  maxHours: number | null;
  minNoticeDays: number | null;
}

export interface RequestDraft {
  onDate?: string | null;
  toDate?: string | null;
  fromTime?: string | null;
  toTime?: string | null;
  hours?: number | null;
  amount?: number | null;
  reason?: string | null;
  attachmentId?: string | null;
}

/**
 * فحصُ طلبٍ على قواعد نوعه.
 *
 * دالّةٌ خالصة: هي الحدُّ الذي يقف عنده ما يُكتب، فتُختبر وحدها وتُستعمل
 * في الخادم وفي الشاشة معًا — فيرى الموظّفُ سببَ المنع قبل أن يرسل لا
 * بعده. وتردّ الأسبابَ كلَّها لا أوّلَها: من يُردّ ثلاث مرّاتٍ لثلاثة
 * أسبابٍ متتالية يظنّ النظام يعانده.
 */
export function checkRequest(
  rules: RequestRules,
  draft: RequestDraft,
  context: { usedThisMonth: number; today: string },
): string[] {
  const errors: string[] = [];

  if (rules.needsDates && !draft.onDate) {
    errors.push('التاريخ مطلوب.');
  }
  if (rules.needsHours && !draft.hours) {
    errors.push('عدد الساعات مطلوب.');
  }
  if (rules.needsAmount && !draft.amount) {
    errors.push('المبلغ مطلوب.');
  }
  if (rules.needsReason && !draft.reason?.trim()) {
    errors.push('السبب مطلوب.');
  }
  if (rules.needsAttachment && !draft.attachmentId) {
    errors.push('المرفق مطلوب — أرفق الإيصال أو المستند.');
  }

  if (rules.maxHours !== null && (draft.hours ?? 0) > rules.maxHours) {
    errors.push(`الساعات أكثر من المسموح (${rules.maxHours} ساعات للطلب).`);
  }

  if (rules.maxPerMonth !== null && context.usedThisMonth >= rules.maxPerMonth) {
    errors.push(
      `بلغتَ حدَّ هذا النوع لهذا الشهر (${rules.maxPerMonth}). المسحوبُ والمرفوضُ لا يُحسبان.`,
    );
  }

  /*
   * المهلةُ تُقاس بالأيّام لا بالساعات.
   *
   * «قبل يومين» تعني يومين تقويميّين لا ثمانيًا وأربعين ساعة: من طلب
   * مساءَ الاثنين لصباح الأربعاء أعطى يومين في عرف الناس.
   */
  if (rules.minNoticeDays !== null && draft.onDate) {
    const days = daysBetween(context.today, draft.onDate);
    if (days < rules.minNoticeDays) {
      errors.push(
        `هذا النوع يُطلب قبل ${rules.minNoticeDays} ${rules.minNoticeDays === 2 ? 'يومين' : 'أيام'} على الأقلّ.`,
      );
    }
  }

  if (draft.onDate && draft.toDate && draft.toDate < draft.onDate) {
    errors.push('تاريخ النهاية قبل تاريخ البداية.');
  }

  if (draft.fromTime && draft.toTime && draft.toTime <= draft.fromTime) {
    errors.push('وقت النهاية قبل وقت البداية.');
  }

  return errors;
}

/** الفرقُ بالأيّام بين تاريخين بصيغة سنة-شهر-يوم. */
export function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00.000Z`);
  const b = Date.parse(`${to}T00:00:00.000Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86_400_000);
}

/** الساعاتُ بين وقتين بصيغة ساعة:دقيقة — تُقرَّب إلى ربع ساعة. */
export function hoursBetween(from: string, to: string): number {
  const m = (t: string) => {
    const [h, mm] = t.split(':').map((x) => Number(x) || 0);
    return (h ?? 0) * 60 + (mm ?? 0);
  };
  const diff = m(to) - m(from);
  if (diff <= 0) return 0;
  return Math.round((diff / 60) * 4) / 4;
}
